import { useLocationAlarmUpdates } from '@hakimo-ui/hakimo/data-access';
import { QueryResult, Timeline } from '@hakimo-ui/hakimo/ui-elements';
import { useExportCsv, useIsSupportRole } from '@hakimo-ui/hakimo/util';
import { Alert, Button } from '@hakimo-ui/shared/ui-base';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

import { useMemo, useState } from 'react';

import { UnifiedMonitoringProduct } from '@hakimo-ui/hakimo/types';
import {
  useAlarmGroupPlayback,
  useAlarmGroupStatic,
} from '../alarm-provider/AlarmGroupProvider';
import { getFormattedExportData } from './extraDetailsUtil';
import { getLocationAlarmUpdateEvents } from './util';

const REFETCH_INTERVAL = 60000;

export interface Props {
  locationAlarmId: string;
  locationName: string;
  alarmGroupTimestamp: string;
  product: UnifiedMonitoringProduct;
  locationTimezone: string;
  title?: string;
}

export function AlarmUpdates(props: Props) {
  const {
    locationAlarmId,
    locationName,
    alarmGroupTimestamp,
    product,
    title,
    locationTimezone,
  } = props;
  const { dataVersion, isScanLike } = useAlarmGroupStatic();
  const query = useLocationAlarmUpdates(
    locationAlarmId,
    REFETCH_INTERVAL,
    alarmGroupTimestamp,
    product,
    undefined,
    false,
    dataVersion
  );
  const isSupportRole = useIsSupportRole();
  const { exportCsv } = useExportCsv();
  const [isExporting, setIsExporting] = useState(false);
  const { allCombinedAlarms } = useAlarmGroupPlayback();

  const criticalAlarmIds = useMemo(() => {
    // Severity-based critical marking applies to scan-shaped data; v3 delivers
    // it as remote-guarding, so gate on isScanLike (v2 = product === SCAN).
    if (!isScanLike) return undefined;
    return new Set(
      allCombinedAlarms
        .filter((alarm) => alarm.severity !== 'low')
        .map((alarm) => alarm.id)
    );
  }, [allCombinedAlarms, isScanLike]);

  const handleExport = (events: any[]) => {
    setIsExporting(true);
    try {
      if (!events.length || !query.data?.items) {
        throw new Error('No data to export');
      }

      const formattedData = getFormattedExportData(events, query.data);
      if (!Array.isArray(formattedData) || formattedData.length === 0) {
        throw new Error('Formatted data is invalid or empty');
      }
      exportCsv({
        formattedData,
        fileName: `alarm-updates-${locationAlarmId}-${locationName}.csv`,
        fileType: 'csv',
      });
    } catch (error) {
      throw new Error(
        `Export Failed: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <QueryResult queryResult={query} loadingText="">
      {(data) => {
        const events = getLocationAlarmUpdateEvents(
          data,
          isSupportRole,
          product,
          false,
          locationAlarmId,
          locationTimezone,
          criticalAlarmIds
        );
        return (
          <div className="dark:bg-ondark-bg-1 bg-onlight-bg-1 rounded-lg p-2">
            <div className="flex items-center justify-between">
              <h2 className="flex h-9 items-center pl-1 font-bold">
                {title ?? 'Alarm Updates'}
              </h2>
              {isSupportRole && (
                <Button
                  variant="icon"
                  onClick={() => {
                    handleExport(events);
                  }}
                  loading={isExporting}
                  title="Export Alarm Updates"
                  disabled={!query.data}
                >
                  <ArrowDownTrayIcon className="w-5" />
                </Button>
              )}
            </div>
            {events.length > 0 ? (
              <div className=" max-h-[36rem] overflow-auto py-2 pl-1">
                <Timeline events={events} />
              </div>
            ) : (
              <div className="pt-4">
                <Alert>No updates</Alert>
              </div>
            )}
          </div>
        );
      }}
    </QueryResult>
  );
}

export default AlarmUpdates;
